import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  StorageProvider,
  CreateStorageRequest,
  UpdateStorageRequest,
  ScratchUploadResponse,
  StorageCopyRequest,
  StorageCopyResponse,
} from '@/types/storage';
import { getAccessToken } from '@/utils/jwt';

interface ProvidersResponse {
  success: boolean;
  providers: StorageProvider[];
}

interface ProviderResponse {
  success: boolean;
  provider: StorageProvider;
}

interface HealthResponse {
  success: boolean;
  healthy: boolean;
}

interface ProviderUploadResponse {
  success: boolean;
  file: { path: string; size: number; content_type: string };
}

export const storageApi = createApi({
  reducerPath: 'storageApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['StorageProvider'],
  endpoints: (builder) => ({
    listStorageProviders: builder.query<StorageProvider[], undefined>({
      query: () => '/storage/providers',
      transformResponse: (response: ProvidersResponse) => response.providers,
      providesTags: ['StorageProvider'],
    }),
    createStorageProvider: builder.mutation<StorageProvider, CreateStorageRequest>({
      query: (body) => ({ url: '/storage/providers', method: 'POST', body }),
      transformResponse: (response: ProviderResponse) => response.provider,
      invalidatesTags: ['StorageProvider'],
    }),
    updateStorageProvider: builder.mutation<
      StorageProvider,
      { id: string; body: UpdateStorageRequest }
    >({
      query: ({ id, body }) => ({ url: `/storage/providers/${id}`, method: 'PATCH', body }),
      transformResponse: (response: ProviderResponse) => response.provider,
      invalidatesTags: ['StorageProvider'],
    }),
    deleteStorageProvider: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/storage/providers/${id}`, method: 'DELETE' }),
      invalidatesTags: ['StorageProvider'],
    }),
    healthCheckStorageProvider: builder.mutation<HealthResponse, string>({
      query: (id) => ({ url: `/storage/providers/${id}/health`, method: 'POST' }),
      invalidatesTags: ['StorageProvider'],
    }),
    scratchUpload: builder.mutation<ScratchUploadResponse, { chatId: string; file: File }>({
      query: ({ chatId, file }) => {
        const formData = new FormData();
        formData.append('chat_id', chatId);
        formData.append('file', file);
        return {
          url: '/storage/scratch/upload',
          method: 'POST',
          body: formData,
        };
      },
    }),
    storageCopy: builder.mutation<StorageCopyResponse, StorageCopyRequest>({
      query: (body) => ({
        url: '/storage/copy',
        method: 'POST',
        body,
      }),
    }),
    providerUpload: builder.mutation<
      ProviderUploadResponse,
      { providerId: string; path: string; file: File }
    >({
      query: ({ providerId, path, file }) => {
        const formData = new FormData();
        formData.append('path', path);
        formData.append('file', file);
        return {
          url: `/storage/providers/${providerId}/files/upload`,
          method: 'POST',
          body: formData,
        };
      },
    }),
  }),
});

export const {
  useListStorageProvidersQuery,
  useCreateStorageProviderMutation,
  useUpdateStorageProviderMutation,
  useDeleteStorageProviderMutation,
  useHealthCheckStorageProviderMutation,
  useScratchUploadMutation,
  useStorageCopyMutation,
  useProviderUploadMutation,
} = storageApi;
